import React, { useEffect } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeading,
  TableBody,
  TableCell,
  useQueryParams,
} from '@insurancemenu/imenu-components-lib';
import { getClassIcon } from 'global/utils/election/subscriberClassHelper';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import ProductLineTitle from 'components/common/general/ProductLineTitle';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { replaceHtmlBreaks } from '@insurancemenu/imenu-components-lib/dist/helpers/replaceHtmlBreaks';
import { ProductInfo, PlanDetails } from 'types/electionService';
import ProductCell from '../ProductCell';
import { createStyles } from './styles';

const ProductClassesTable = observer((): JSX.Element => {
  const {
    eeClassesStore: { employeeClasses, loading, products, getChoices },
  } = useRootStore();
  const { periodId } = useParams<{ periodId: string }>();
  const query = useQueryParams();
  const groupId = query.get('groupId') || '';

  useEffect(() => {
    // get plans and ee classes
    void getChoices(groupId, periodId);
  }, []);

  const convertedProducts = products?.map((product: ProductInfo) => toJS(product)).slice();
  const convertedClasses = employeeClasses?.map((classInfo) => toJS(classInfo)).slice();
  const productLinesQuantity = [...new Set(convertedProducts.map(({ productLineId }) => productLineId))].length;
  const maxProductsAmountInProductLine = Math.max(...convertedProducts.map(({ plans }) => plans.length));

  // 4rem - class name column, 28rem - others
  const tableWidth = 5 + productLinesQuantity * 28;
  const classes = createStyles({ maxProductsAmountInProductLine, tableWidth });

  const tableHeaders = convertedProducts.map(({ productLineId }): JSX.Element => {
    return (
      <TableHeading className={classes.tableHeading} key={productLineId}>
        <ProductLineTitle productLineId={productLineId} styles={classes.productLineIdTitle} />
      </TableHeading>
    );
  });

  // Sort classes by type
  const sortedClasses = convertedClasses.sort((a, b) => {
    return a.type > b.type ? -1 : 1;
  });

  const createClassCells = (plans: PlanDetails[], eeClassName: string): JSX.Element[] => {
    return plans.map(({ id: productId, name: productName, eligibleClasses }): JSX.Element => {
      const isInEligibleClasses = eligibleClasses.includes(eeClassName) || eligibleClasses.length === 0;
      return (
        <ProductCell
          key={productId}
          productName={replaceHtmlBreaks(productName)}
          productId={productId}
          eeClass={eeClassName}
          isInEligibleClasses={isInEligibleClasses}
        />
      );
    });
  };

  const tableRows = sortedClasses.map(({ id, name: eeClassName, type }, index) => {
    const showNewTypeDivider = type !== sortedClasses[index - 1]?.type;
    const classRowsContent = convertedProducts.map(({ productLineId, plans }): JSX.Element => {
      return (
        <TableCell className={clsx(classes.tableCell, classes.productCell)} key={productLineId}>
          {createClassCells(plans, eeClassName)}
        </TableCell>
      );
    });

    return (
      <React.Fragment key={id}>
        {showNewTypeDivider && (
          <TableRow key={`divider${eeClassName}-${id}`} className={clsx(classes.tableRow, classes.typeRow)}>
            <TableCell
              className={clsx(classes.tableCell, classes.wideCell, classes.typeRowCategoryCell)}
              key={`type_cell${id}`}
            >
              <div className={clsx(classes.eeClass, classes.leftSticky, classes.typeClassName)}>
                {getClassIcon(type)}
                <span>{type}</span>
              </div>
            </TableCell>
          </TableRow>
        )}
        <TableRow key={`${eeClassName}-${id}`} className={classes.tableRow}>
          <TableCell
            className={clsx(classes.tableCell, classes.wideCell, classes.classCell, classes.leftSticky)}
            key={`name_cell${id}`}
          >
            <div className={classes.eeClass}>
              {getClassIcon(type, eeClassName)}
              <span>{eeClassName}</span>
            </div>
          </TableCell>
          {classRowsContent}
        </TableRow>
      </React.Fragment>
    );
  });

  return (
    <div className={classes.tableWrapper}>
      <Table
        className={classes.table}
        loading={loading}
        totalElements={sortedClasses.length}
        maxHeight="80rem"
        showScrollShadows
        showRowHover
      >
        <TableHeader className={classes.stickyHeader}>
          <TableRow>
            <TableHeading className={clsx(classes.tableHeading, classes.leftSticky, classes.stickyHeading)}>
              Saved Classes
            </TableHeading>
            {tableHeaders}
          </TableRow>
        </TableHeader>
        <TableBody>{tableRows}</TableBody>
      </Table>
    </div>
  );
});

export default ProductClassesTable;
